import type {
  RunSizingCalculationResponse,
  ScenarioContext,
  SizingMetric
} from "@/serverSizing/types";
import { executeAction } from "./adminHttp";

export type ErpMappingStatus = "confirmed" | "unresolved" | "unavailable";
export type ErpSystem = "ecc" | "s4";

export type ErpMaterialMaster = {
  number?: string | null;
  description?: string | null;
  long_description?: string | null;
  material_type?: string | Record<string, unknown> | null;
  prod_hierarchy?: string | null;
  base_uom?: string | Record<string, unknown> | null;
  active?: boolean | null;
  deleted?: boolean | null;
  serialized?: boolean | null;
  standard_offering?: boolean | null;
  extended_to_plants?: string[] | null;
};

export type ErpMaterialMapping = {
  mappingId?: string | null;
  erpSystem: ErpSystem;
  materialNumber: string;
  mappingKind?: "exact" | "variant" | "equivalent" | null;
  confidence?: number | null;
  source?: string | null;
  qualifiers: Record<string, unknown>;
  spyglassPartNumber?: string | null;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  mappingUpdatedAt?: string | null;
  erpVerificationStatus:
    | "not_requested"
    | "verified"
    | "not_found"
    | "inactive"
    | "deleted"
    | "source_unavailable";
  erpMaterial?: ErpMaterialMaster | null;
};

export type MaterialSearchResponse = {
  equipmentType: string;
  items: Array<{
    equipmentType: string;
    id: string;
    manufacturer?: string | null;
    partNumber?: string | null;
    label: string;
    source: string;
    specifications: Record<string, unknown>;
    warnings: unknown[];
    spyglassMaterialId: string;
    erpMappingStatus: ErpMappingStatus;
    erpMaterials: ErpMaterialMapping[];
  }>;
  totalCount: number;
  filtersApplied: Record<string, unknown>;
  search?: string | null;
  limit: number;
  offset: number;
};

export type ErpMaterialMappingResolution = {
  equipmentType: string;
  materials: Array<{
    spyglassMaterialId: string;
    erpMappingStatus: ErpMappingStatus;
    erpMaterials: ErpMaterialMapping[];
  }>;
  coverage: {
    requested: number;
    mapped: number;
    unresolved: number;
  };
  erpValidation: {
    requested: boolean;
    status:
      | "not_requested"
      | "not_applicable"
      | "complete"
      | "partial"
      | "unavailable";
    checkedAt?: string | null;
    warning?: string | null;
    sources: Partial<
      Record<
        ErpSystem,
        {
          status: "complete" | "unavailable";
          requested: number;
          found: number;
          warning?: string | null;
        }
      >
    >;
  };
  source: string;
};

type CandidatePackage = Record<string, unknown> & {
  metadata?: {
    materialIds?: Record<string, string>;
    designPlan?: Record<string, unknown>;
  };
};

type CandidateIssue = {
  code?: string;
  message?: string;
  [key: string]: unknown;
};

export type CandidateResult = {
  traceId?: string | null;
  traceStorage?: "hasura" | "file" | "skipped" | null;
  status: "pass" | "warning" | "fail" | "error" | "rejected";
  warnings: CandidateIssue[];
  hardFailures: CandidateIssue[];
  metrics: Record<string, unknown>;
  candidatePackage: CandidatePackage;
  rejectionReason?: string;
};

export type RankedCandidateOption = {
  id: string;
  lane: "performance" | "runlife" | "cost";
  rank: number;
  score: number;
  scoreBreakdown: Record<string, number>;
  scoreWeights: Record<string, number>;
  confidence: "high" | "medium" | "low";
  pros: string[];
  cons: string[];
  warnings: CandidateIssue[];
  hardFailures: CandidateIssue[];
  metricSummary: Record<string, unknown>;
  traceId?: string | null;
  traceStorage?: "hasura" | "file" | "skipped" | null;
  candidatePackage: CandidatePackage;
  unavailableEnrichment: Record<string, "unavailable">;
};

export type CandidateGenerationResponse = {
  scenarioId: string;
  baselineTraceId?: string;
  baselineTraceStorage?: "hasura" | "file" | "skipped";
  displayableCandidates: CandidateResult[];
  rejectedCandidates: CandidateResult[];
  blockingReasons: CandidateIssue[];
  equipmentIssues?: CandidateIssue[];
  candidateFallbacks?: Array<Record<string, unknown>>;
  catalogCounts: Record<string, number>;
  attemptedCandidateCount?: number;
  rankingStatus?: "available" | "disabled";
  rankingMessage?: string;
  recommendedOption?: RankedCandidateOption | null;
  options?: Record<"performance" | "runlife" | "cost", RankedCandidateOption[]>;
};

export type CompletedSizingLookupResponse = {
  sizings: Array<{
    id: string;
    title?: string | null;
    disposition?: string | null;
    pumping_type?: string | null;
    scenarios: Array<{ id: string; title?: string | null }>;
  }>;
};

export const searchMaterials = (body: Record<string, unknown>) =>
  executeAction<MaterialSearchResponse>("aiDosMaterialSearch", {
    equipmentType: body.equipmentType,
    search: body.search,
    filtersJson: JSON.stringify(body.filters || {}),
    limit: body.limit,
    offset: body.offset
  });

export const resolveErpMaterialMappings = (
  equipmentType: string,
  spyglassMaterialIds: string[],
  validateErpMaterials = false
) =>
  executeAction<ErpMaterialMappingResolution>(
    "aiDosResolveErpMaterialMappings",
    {
      equipmentType,
      spyglassMaterialIds,
      validateErpMaterials
    }
  );

export type ErpMappingAutomationMode = "deterministic" | "ai_assisted";
export type ErpMappingAiReviewStatus = {
  enabled: boolean;
  modelId?: string | null;
  region: string;
  reviewVersion: string;
  disabledReason?: string | null;
};
export type ErpMappingCandidateDecision =
  | "proposed"
  | "auto_confirmed"
  | "approved"
  | "rejected";

export type ErpMappingRun = {
  id: string;
  equipmentType: string;
  mode: ErpMappingAutomationMode;
  status: "running" | "completed" | "failed";
  algorithmVersion: string;
  modelId?: string | null;
  requestedBy?: string | null;
  requestConfig: Record<string, unknown>;
  catalogCount: number;
  candidateCount: number;
  autoConfirmedCount: number;
  unresolvedCount: number;
  errorMessage?: string | null;
  startedAt: string;
  completedAt?: string | null;
};

export type ErpMappingCandidate = {
  id: string;
  runId: string;
  equipmentType: string;
  spyglassMaterialId: string;
  spyglassPartNumber?: string | null;
  spyglassLabel: string;
  erpSystem: ErpSystem;
  erpMaterialNumber: string;
  erpDescription?: string | null;
  erpProductHierarchy?: string | null;
  mappingKind: "exact" | "variant" | "equivalent";
  rank: number;
  deterministicScore: number;
  aiScore?: number | null;
  finalScore: number;
  scoreBreakdown: Record<string, number>;
  evidence: Record<string, unknown>;
  conflicts: Array<Record<string, unknown>>;
  qualifiers: Record<string, unknown>;
  decisionStatus: ErpMappingCandidateDecision;
  decisionReason?: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

const runErpMappingAutomationOperation = <T>(
  operation: string,
  payload: Record<string, unknown>
) =>
  executeAction<T>("aiDosErpMaterialMappingAutomation", {
    operation,
    payloadJson: JSON.stringify(payload)
  });

export const getErpMappingAiReviewStatus = () =>
  runErpMappingAutomationOperation<ErpMappingAiReviewStatus>(
    "ai_review_status",
    {}
  );

export const generateErpMappingCandidates = (payload: {
  equipmentType: string;
  mode: ErpMappingAutomationMode;
  search?: string;
  summitOnly?: boolean;
  includeBomEvidence?: boolean;
  discoverVariants?: boolean;
  erpSearchPage?: number;
  bomComponentNumbers?: string[];
  bomPlant?: string;
  erpSystems?: ErpSystem[];
  limit?: number;
  offset?: number;
}) =>
  runErpMappingAutomationOperation<{
    run: ErpMappingRun;
    catalogTotalCount: number;
  }>("run", payload);

export const inspectErpMapping = (payload: {
  equipmentType: string; catalogId: string; erpSystem: ErpSystem;
  materialNumber: string; plant?: string; previousFingerprint?: string;
  includeInventory?: boolean;
  assemblyRequirements?: Record<string, string | number | boolean>;
}) => runErpMappingAutomationOperation<Record<string, unknown>>("inspect_mapping", payload);

export const compareErpAssemblyAvailability = (payload: {
  equipmentType: string; catalogId: string; erpSystem: ErpSystem;
  requirements: Record<string, string | number | boolean>; plants: string[];
}) => runErpMappingAutomationOperation<Record<string, unknown>>("assembly_availability", payload);

export const compareScenarioAssemblyAvailability = (payload: {
  scenarioId: string; erpSystem: ErpSystem; plants: string[];
}) => runErpMappingAutomationOperation<Record<string, unknown>>("scenario_availability", payload);

export const discoverErpBomParents = (payload: {
  erpSystem: ErpSystem; componentNumbers: string[]; plant?: string;
}) => runErpMappingAutomationOperation<Record<string, unknown>>("bom_parents", payload);

export const listErpMappingCandidates = (payload: {
  equipmentType: string;
  decisionStatus?: ErpMappingCandidateDecision | "";
  erpSystem?: ErpSystem;
  runId?: string;
  limit?: number;
  offset?: number;
}) =>
  runErpMappingAutomationOperation<{
    candidates: ErpMappingCandidate[];
    totalCount: number;
    limit: number;
    offset: number;
  }>("list_candidates", payload);

export const listErpMappingRuns = (equipmentType: string, limit = 10) =>
  runErpMappingAutomationOperation<{ runs: ErpMappingRun[] }>("list_runs", {
    equipmentType,
    limit
  });

export const decideErpMappingCandidate = (
  candidateId: string,
  decision: "approve" | "reject",
  reason?: string
) =>
  runErpMappingAutomationOperation<{ candidate: ErpMappingCandidate }>(
    decision,
    { candidateId, reason }
  );

export const generateCandidates = (body: Record<string, unknown>) =>
  executeAction<CandidateGenerationResponse>("aiDosGenerateCandidates", body);

export const lookupCompletedSizing = (sizingId: string) => {
  return executeAction<CompletedSizingLookupResponse>("aiDosCompletedSizings", {
    limit: 1,
    dispositions: ["accepted", "closed"],
    sizingId
  });
};

export const listCompletedSizings = (limit = 100) =>
  executeAction<CompletedSizingLookupResponse>("aiDosCompletedSizings", {
    limit,
    dispositions: ["accepted", "closed"]
  });

export type RunSizingCalculationActionInput = {
  scenarioId: string;
  calculationMode?:
    | "baseline_existing_equipment"
    | "candidate_verification"
    | "parity_fixture";
  debug?: boolean;
  persistTrace?: boolean;
  candidatePackageJson?: string;
  prepareCandidate?: boolean;
  validateDesignInputs?: boolean;
  agentRunId?: string;
  toolCallId?: string;
  runtimeSessionId?: string;
};

export const runSizingCalculationAction = (
  input: RunSizingCalculationActionInput
) =>
  executeAction<RunSizingCalculationResponse>(
    "aiDosRunSizingCalculation",
    input
  );

export type SizingTraceResponse = {
  traceId: string;
  status: "pass" | "warning" | "fail";
  metrics: Record<string, SizingMetric>;
  warnings: Array<Record<string, unknown>>;
  hardFailures: Array<Record<string, unknown>>;
};

export const fetchSizingTrace = (traceId: string) =>
  executeAction<SizingTraceResponse>("aiDosSizingTrace", { traceId });

export const fetchSizingReportContext = (scenarioId: string) =>
  executeAction<ScenarioContext>("aiDosSizingReportContext", { scenarioId });

export type AgentCoreAdminOperation =
  | "evaluation_cases"
  | "save_sizing_review"
  | "list_sizing_reviews"
  | "status"
  | "invoke_runtime"
  | "invoke_calculation_poc"
  | "get_calculation_poc_audit"
  | "list_calculation_poc_audits"
  | "list_tools"
  | "call_tool"
  | "recommendation_config"
  | "update_recommendation_config";

export type AgentCoreAdminResponse = Record<string, unknown>;

export type AgentCoreAuditStatus = "running" | "completed" | "failed";

export type AgentCoreAuditRunSummary = {
  agentRunId: string;
  runtimeSessionId: string;
  scenarioId: string;
  actorId?: string | null;
  contractVersion: string;
  agentVersion: string;
  calculationMode: string;
  status: AgentCoreAuditStatus;
  errorCode?: string | null;
  errorMessage?: string | null;
  elapsedMs?: number | null;
  responseMetadata: Record<string, unknown>;
  startedAt: string;
  completedAt?: string | null;
  toolCallCount: number;
  calculationTraceCount: number;
};

export type AgentCoreAuditRecord = AgentCoreAuditRunSummary & {
  requestHash: string;
  toolCalls: Array<{
    toolCallId: string;
    toolName: string;
    targetName: string;
    inputHash: string;
    outputHash?: string | null;
    status: AgentCoreAuditStatus;
    calculationTraceId?: string | null;
    errorCode?: string | null;
    errorMessage?: string | null;
    elapsedMs?: number | null;
    startedAt: string;
    completedAt?: string | null;
  }>;
  calculationTraces: Array<{
    traceId: string;
    engineVersion: string;
    engineHash: string;
    status: string;
    elapsedMs?: number | null;
    createdAt: string;
  }>;
};

export type AgentCoreAuditListResponse = {
  runs: AgentCoreAuditRunSummary[];
  totalCount: number;
  limit: number;
  offset: number;
};

export const runAgentCoreAdminOperation = <T = AgentCoreAdminResponse>(
  operation: AgentCoreAdminOperation,
  payload: Record<string, unknown> = {}
) =>
  executeAction<T>("aiDosAgentCoreAdmin", {
    operation,
    payloadJson: JSON.stringify(payload)
  });
